import { DeepPartial } from 'types/DeepPartial';
import { UserEdit, UserEditOptions } from 'types/interfaces/user/UserEdit';
import { UserPhoto } from 'types/interfaces/user/UserPhoto';
import { UserProfile } from 'types/interfaces/user/UserProfile';
import { UserVideo } from 'types/interfaces/user/UserVideo';

import { httpClient } from './httpClient';

type UserEditPayload = DeepPartial<
  Pick<UserEdit, 'username' | 'age' | 'details'> & { city: number }
>;

interface UserEditResponsse {
  data: UserEdit;
  options: UserEditOptions;
}

export interface UserVideosResponse {
  videos: UserVideo[];
  videos_access: number[];
}

export interface UploadPhotoPayload {
  photo: File;
}

export interface UploadVideoPayload {
  video: File;
}

export interface ChangeUserLanguagePayload {
  lang: string;
}

export interface ChangeUserLanguageResponse {
  site_language: string;
}

export const UserProfileApi = {
  async fetchPublicProfile(payload: { userId: number }) {
    const { data } = await httpClient.get<UserProfile>(
      `/profile/${payload.userId}`
    );

    return data;
  },

  async fetchEditProfile() {
    const { data } = await httpClient.get<UserEditResponsse>('/user');

    return data;
  },

  async editUserInfo(payload: UserEditPayload) {
    const { data } = await httpClient.put<UserEditResponsse>(
      '/user/edit',
      payload
    );

    return data;
  },

  async setMainPhoto(payload: { photoId: number }) {
    return httpClient.get<unknown>(`/user/photo/set-main/${payload.photoId}`);
  },

  async deletePhoto(payload: { photoId: number }) {
    return httpClient.delete<unknown>(`/user/photo/${payload.photoId}`);
  },

  async deleteVideo(payload: { videoId: number }) {
    return httpClient.delete<unknown>(`/user/video/${payload.videoId}`);
  },

  async uploadPhoto(payload: UploadPhotoPayload) {
    const formData = new FormData();
    formData.append('photo', payload.photo);

    const { data } = await httpClient.post<UserPhoto>(
      '/user/photo/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return data;
  },

  async uploadVideo(payload: UploadVideoPayload) {
    const formData = new FormData();
    formData.append('video', payload.video);

    const { data } = await httpClient.post<UserVideo>(
      '/user/video/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return data;
  },

  async fetchUserVideos(payload: { userId: number }) {
    const { data } = await httpClient.get<UserVideosResponse>(
      `/user/${payload.userId}/videos`
    );

    return data;
  },

  async fetchUserVideo(payload: { videoId: number }) {
    const { data } = await httpClient.get<UserVideo>(
      `/videos/${payload.videoId}`
    );

    return data;
  },

  async trackVideoSeen(payload: { videoId: number }) {
    await httpClient.post(`/user/video/${payload.videoId}/seen`);
  },

  async changeUserLanguage(payload: ChangeUserLanguagePayload) {
    const { data } = await httpClient.patch<ChangeUserLanguageResponse>(
      '/user/settings/site-language',
      {
        site_language: payload.lang,
      }
    );

    return data;
  },

  async checkIsUserBanned({ userId }: { userId: number }) {
    const { data } = await httpClient.get<{ banned: boolean }>(
      `user/check-is-banned/${userId}`
    );

    return data;
  },
};
